import { gtmPushData, getMakeIcon } from "@talladega/helpers";
import { convert, LocalDate } from "@js-joda/core";
import { Modal } from "@talladega/components";
import { ArrowRightIcon, CheckCircleIcon } from "@talladega/icons";
import classNames from "classnames";
import { useRouter } from "next/router";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Purchase } from "../types/Purchase";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  purchases: Purchase[];
};
const ResumePurchaseModal = ({ isOpen, onClose, purchases }: Props) => {
  const { t } = useTranslation(["common"]);
  const { push, query, locale = "en" } = useRouter();

  const { handleSubmit, control } = useForm<{ selectedId: string }>();

  const onDealSelected = ({ selectedId }: { selectedId: string }) => {
    gtmPushData({ event: "ICO", description: "ICO Resume Modal Resume" });
    push(
      {
        pathname: "/confirm",
        query: { ...query, dealId: selectedId },
      },
      undefined,
      { shallow: true }
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("common:resume")}
      alignment="center"
      fitWidthToContent
    >
      <form
        onSubmit={handleSubmit(onDealSelected)}
        className="w-fit sm:w-[550px]"
      >
        <div className="text-left p-4 -mb-10">
          <p className="text-lg font-semibold text-primary">
            {t("common:instant_cash_offers")}
          </p>
          <p className="text-sm font-normal text-primary">
            {t("common:here_is_the_list_of_offers")}
          </p>

          <p className="text-sm font-medium text-primary mt-5">
            {t("common:select_one_vehicle")}
          </p>

          <Controller
            name="selectedId"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <ul className="mt-3 flex flex-col gap-6">
                {purchases.map((p) => {
                  const tradeIn = p.tradeIns[0];
                  const MakeIcon = getMakeIcon(tradeIn.make ?? "");

                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => onChange(p.id)}
                        className="w-full"
                      >
                        <div
                          className={classNames(
                            "bg-white rounded-xl block sm:hidden gap-3 p-3 shadow border-2 w-full",
                            {
                              "border-primary-400": p.id === value,
                              "border-transparent": p.id !== value,
                            }
                          )}
                        >
                          <MakeIcon className="text-6xl p-1 rounded-full bg-gray-100 mx-auto block" />
                          <p className="text-primary text-lg font-semibold my-1">
                            {tradeIn.year} {tradeIn.make} {tradeIn.model}{" "}
                            {tradeIn.trim}
                          </p>
                          <p className="text-sm font-normal">
                            {tradeIn.mileage
                              ?.toLocaleString(`${locale}-CA`, {
                                style: "unit",
                                unit: "kilometer",
                                unitDisplay: "short",
                              })
                              .toLocaleUpperCase()}
                            {tradeIn.vin && <> | {tradeIn.vin}</>}
                          </p>
                          <div className="text-brand-secondary text-2xl font-bold my-3">
                            {tradeIn.finalOfferPrice?.toLocaleString(
                              `${locale}-CA`,
                              {
                                style: "currency",
                                currency: "CAD",
                                maximumFractionDigits: 0,
                              }
                            )}
                          </div>
                          <hr className="border-b border-secondary my-3" />
                          <div className="flex flex-row flex-wrap gap-3 w-4/5">
                            <div className="bg-gray-100 px-2 py-0.5 rounded-full text-sm text-tertiary font-semibold inline-flex gap-1 items-center">
                              {p.dropOffDetails
                                ?.dropOffLocationAppointmentAt ? (
                                <CheckCircleIcon className="text-primary-600 text-lg" />
                              ) : (
                                <input
                                  type="radio"
                                  readOnly
                                  className="cursor-pointer focus:!border-text-light-200 bg-transparent"
                                />
                              )}{" "}
                              {t("common:drop_off_date")}
                            </div>
                            <div className="bg-gray-100 px-2 py-0.5 rounded-full text-sm text-tertiary font-semibold inline-flex gap-1 items-center">
                              {p.customers[0]?.payoutEmailConfirmed ? (
                                <CheckCircleIcon className="text-primary-600 text-lg" />
                              ) : (
                                <input
                                  type="radio"
                                  readOnly
                                  className="cursor-pointer focus:!border-text-light-200 bg-transparent"
                                />
                              )}{" "}
                              {t("common:payment_details")}
                            </div>
                            <div className="bg-gray-100 px-2 py-0.5 rounded-full text-sm text-tertiary font-semibold inline-flex gap-1 items-center">
                              {p.documentSummary.missingForUpload === 0 ? (
                                <CheckCircleIcon className="text-primary-600 text-lg" />
                              ) : (
                                <input
                                  type="radio"
                                  readOnly
                                  className="cursor-pointer focus:!border-text-light-200 bg-transparent"
                                />
                              )}{" "}
                              {t("common:documents")}
                            </div>
                          </div>
                          {tradeIn.offerInvalidAfter && (
                            <p className="text-sm text-left m-3">
                              *{" "}
                              {t(
                                "common:expires_on_date_or_when_mileage_exceeds",
                                {
                                  date: convert(
                                    LocalDate.parse(tradeIn.offerInvalidAfter)
                                  )
                                    .toDate()
                                    .toLocaleString(`${locale}-CA`, {
                                      dateStyle: "long",
                                    }),
                                }
                              )}
                            </p>
                          )}
                        </div>
                        <div
                          className={classNames(
                            "bg-white rounded-xl hidden sm:flex gap-3 p-3 shadow border-2",
                            {
                              "border-primary-400": p.id === value,
                              "border-transparent": p.id !== value,
                            }
                          )}
                        >
                          <div>
                            <MakeIcon className="text-6xl p-1 rounded-full bg-gray-100" />
                          </div>
                          <div className="text-left">
                            <div className="flex justify-between">
                              <div>
                                <p className="font-semibold text-lg text-primary">
                                  {tradeIn.year} {tradeIn.make} {tradeIn.model}{" "}
                                  {tradeIn.trim}
                                </p>
                                <p className="text-sm font-normal">
                                  {tradeIn.mileage
                                    ?.toLocaleString(`${locale}-CA`, {
                                      style: "unit",
                                      unit: "kilometer",
                                      unitDisplay: "short",
                                    })
                                    .toLocaleUpperCase()}
                                  {tradeIn.vin && <> | {tradeIn.vin}</>}
                                </p>
                              </div>
                              <div className="text-brand-secondary text-xl font-bold">
                                {tradeIn.finalOfferPrice?.toLocaleString(
                                  `${locale}-CA`,
                                  {
                                    style: "currency",
                                    currency: "CAD",
                                    maximumFractionDigits: 0,
                                  }
                                )}
                              </div>
                            </div>
                            <div className="flex gap-3 my-3">
                              <div className="bg-gray-100 px-2 py-0.5 rounded-full text-secondary text-sm flex gap-1 items-center">
                                {p.dropOffDetails
                                  ?.dropOffLocationAppointmentAt ? (
                                  <CheckCircleIcon className="text-primary-600 text-lg" />
                                ) : (
                                  <input
                                    type="radio"
                                    readOnly
                                    className="cursor-pointer focus:!border-text-light-200 bg-transparent"
                                  />
                                )}{" "}
                                {t("common:drop_off_date")}
                              </div>
                              <div className="bg-gray-100 px-2 py-0.5 rounded-full text-secondary text-sm flex gap-1 items-center">
                                {p.customers[0]?.payoutEmailConfirmed ? (
                                  <CheckCircleIcon className="text-primary-600 text-lg" />
                                ) : (
                                  <input
                                    type="radio"
                                    readOnly
                                    className="cursor-pointer focus:!border-text-light-200 bg-transparent"
                                  />
                                )}{" "}
                                {t("common:payment_details")}
                              </div>
                              <div className="bg-gray-100 px-2 py-0.5 rounded-full text-secondary text-sm flex gap-1 items-center">
                                {p.documentSummary.missingForUpload === 0 ? (
                                  <CheckCircleIcon className="text-primary-600 text-lg" />
                                ) : (
                                  <input
                                    type="radio"
                                    readOnly
                                    className="cursor-pointer focus:!border-text-light-200 bg-transparent"
                                  />
                                )}{" "}
                                {t("common:documents")}
                              </div>
                            </div>
                            {tradeIn.offerInvalidAfter && (
                              <p className="text-sm">
                                *{" "}
                                {t(
                                  "common:expires_on_date_or_when_mileage_exceeds",
                                  {
                                    date: convert(
                                      LocalDate.parse(tradeIn.offerInvalidAfter)
                                    )
                                      .toDate()
                                      .toLocaleString(`${locale}-CA`, {
                                        dateStyle: "long",
                                      }),
                                  }
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          />
          <Modal.Buttons
            primaryButtonText={t("common:resume")}
            primaryButtonRightIcon={<ArrowRightIcon />}
            isPrimarySubmit
            secondaryButtonText={t("common:ill_continue_later")}
            secondaryButtonFill="link"
            onSecondaryClick={() => {
              gtmPushData({
                event: "ICO",
                description: "ICO Resume Modal Continue Later",
              });
              onClose();
            }}
          />
        </div>
      </form>
    </Modal>
  );
};

export default ResumePurchaseModal;
